import {Box, IconButton, Stack, Typography} from '@mui/material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import CheckRounded from '@mui/icons-material/CheckRounded';
import {alpha} from '@mui/material/styles';
import {useEffect, useRef, useState} from 'react';
import {OrderResponseTemplateDto} from '@mybake/ui/models/Order/IOrderGuid';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

interface Props {
    orderNumber: string;
    responseTemplates?: OrderResponseTemplateDto[];
    onCopy: (text: string) => void | Promise<void>;
}

function CopyButton({text, label, onCopy}: {text: string; label: string; onCopy: Props['onCopy']}) {
    const [copied, setCopied] = useState(false);
    const timeout = useRef<ReturnType<typeof setTimeout>>();
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(timeout.current);
        };
    }, []);
    const copy = async () => {
        try {
            await onCopy(text);
            if (!mounted.current) return;
            setCopied(true);
            clearTimeout(timeout.current);
            timeout.current = setTimeout(() => setCopied(false), 2000);
        } catch {
            // Keep the copy icon when copying fails, so the user can retry.
        }
    };
    return <IconButton aria-label={label} title={label} onClick={copy} sx={theme => ({
        width: 40, height: 40, borderRadius: '12px',
        border: '1px solid', borderColor: copied ? 'primary.main' : alpha(theme.palette.primary.main, 0.35),
        color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, copied ? 0.12 : 0.04),
        transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow']),
        '&:hover': {bgcolor: alpha(theme.palette.primary.main, 0.12), borderColor: 'primary.main'},
        '&.Mui-focusVisible': {outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 3},
    })}>{copied ? <CheckRounded fontSize="small"/> : <ContentCopy fontSize="small"/>}</IconButton>;
}

export function OrderSaveResponse({orderNumber, responseTemplates = [], onCopy}: Props) {
    const {t} = useAppTranslation();
    const templates = [...responseTemplates].sort((a, b) => a.sortOrder - b.sortOrder);
    return <Stack spacing={2}>
        <Typography component="h1" variant="h5" fontWeight={700}>{t('orders:order_created_success')}</Typography>
        <Typography color="text.secondary">{t('orders:order_created_copy_hint')}</Typography>
        <Box sx={{display: 'grid', gridTemplateColumns: 'minmax(0, max-content) 40px', alignItems: 'start', columnGap: 1, rowGap: 2, maxWidth: '100%'}}>
        <Typography sx={{alignSelf: 'center', overflowWrap: 'anywhere'}}>{t('orders:order_number')} {orderNumber}</Typography>
        <CopyButton text={orderNumber} label={`${t('home:copy')} ${orderNumber}`} onCopy={onCopy}/>
        {templates.map((template, index) => <Box key={`${template.language}-${index}`} sx={{display: 'contents'}}>
            <Box component="details" sx={{width: 0, minWidth: '100%'}}>
                <Box component="summary" sx={{cursor: 'pointer', py: 1}}>{template.language}</Box>
                <Typography sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>{template.text}</Typography>
            </Box>
            <CopyButton text={template.text} label={`${t('home:copy')} ${template.language}`} onCopy={onCopy}/>
        </Box>)}
        </Box>
    </Stack>;
}
